import React, { useState, useMemo } from "react";
import { Card, CardBody, Badge, Table, Button } from "reactstrap";
import { Link, useHistory } from "react-router-dom";
import moment from "moment";
import { useTranslation } from "react-i18next";

const LiveWaitingRoomWidget = ({ queue = [] }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [filterTab, setFilterTab] = useState("all"); // 'all', 'in_chair', 'waiting', 'scheduled', 'completed'

  // Categorize status for each queue item
  const categorizedQueue = useMemo(() => {
    return (queue || []).map((item, idx) => {
      let statusKey = "scheduled";
      let statusLabel = t("Scheduled");
      let statusBadgeColor = "primary";
      let statusIcon = "bx bx-calendar";

      if (item.completed_time) {
        statusKey = "completed";
        statusLabel = t("Completed");
        statusBadgeColor = "success";
        statusIcon = "bx bx-check-double";
      } else if (item.attened_time) {
        statusKey = "in_chair";
        statusLabel = t("In Chair");
        statusBadgeColor = "info";
        statusIcon = "bx bx-play-circle";
      } else if (item.is_visited === 1 || item.arravel_time) {
        statusKey = "waiting";
        statusLabel = t("Waiting");
        statusBadgeColor = "warning";
        statusIcon = "bx bx-time-five";
      }

      return {
        ...item,
        statusKey,
        statusLabel,
        statusBadgeColor,
        statusIcon
      };
    });
  }, [queue, t]);

  // Counts
  const counts = useMemo(() => {
    const c = { all: categorizedQueue.length, in_chair: 0, waiting: 0, scheduled: 0, completed: 0 };
    categorizedQueue.forEach(item => {
      if (c[item.statusKey] !== undefined) {
        c[item.statusKey]++;
      }
    });
    return c;
  }, [categorizedQueue]);

  // Filtered List
  const filteredQueue = useMemo(() => {
    if (filterTab === "all") return categorizedQueue;
    return categorizedQueue.filter(item => item.statusKey === filterTab);
  }, [categorizedQueue, filterTab]);

  return (
    <Card className="border-0 shadow-sm mb-4 h-100">
      <CardBody className="p-3">
        {/* Header */}
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-2.5 pb-2 border-bottom gap-2">
          <div className="d-flex align-items-center">
            <div className="avatar-xs me-2">
              <span className="avatar-title rounded bg-warning-subtle text-warning font-size-16">
                <i className="bx bx-chair" />
              </span>
            </div>
            <div>
              <h5 className="card-title mb-0 font-size-14 fw-bold">
                {t("Live Waiting Room & Operatory Queue")}
              </h5>
              <div className="text-muted font-size-11">
                {t("Real-time patient flow & chair occupancy for today")} ({moment().format("DD MMM YYYY")})
              </div>
            </div>
          </div>

          <Link
            to="/appointment"
            className="btn btn-sm btn-outline-primary font-size-11 px-2.5 py-1 rounded-pill"
          >
            {t("Appointment Desk")} <i className="bx bx-chevron-right ms-1" />
          </Link>
        </div>

        {/* Quick Status Filter Pills */}
        <div className="d-flex align-items-center gap-1.5 mb-2.5 flex-wrap">
          {[
            { key: "all", label: t("All Today"), count: counts.all, color: "secondary" },
            { key: "in_chair", label: `💺 ${t("In Chair")}`, count: counts.in_chair, color: "info" },
            { key: "waiting", label: `⏳ ${t("Waiting")}`, count: counts.waiting, color: "warning" },
            { key: "scheduled", label: `📅 ${t("Scheduled")}`, count: counts.scheduled, color: "primary" },
            { key: "completed", label: `✓ ${t("Completed")}`, count: counts.completed, color: "success" }
          ].map(tab => (
            <button
              key={tab.key}
              type="button"
              className={`btn btn-sm py-0.5 px-2 font-size-11 rounded-pill border ${
                filterTab === tab.key
                  ? `btn-${tab.color} text-white font-weight-bold`
                  : "btn-light text-dark bg-white"
              }`}
              onClick={() => setFilterTab(tab.key)}
            >
              {tab.label} <span className="badge bg-light text-dark ms-0.5 font-size-10">{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Table / Empty State */}
        {filteredQueue.length === 0 ? (
          <div className="text-center py-4 text-muted bg-light rounded">
            <i className="bx bx-check-circle font-size-24 text-success mb-1 d-block" />
            <div className="font-size-13 fw-medium">{t("No patients in this queue status")}</div>
            <div className="font-size-11 text-muted">
              {filterTab === "all"
                ? t("No appointments scheduled for today.")
                : t("Switch tabs or visit the Appointment Desk to manage check-ins.")}
            </div>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover className="align-middle table-nowrap mb-0 font-size-12">
              <thead className="table-light text-muted">
                <tr>
                  <th style={{ width: "45px" }}>{t("Token")}</th>
                  <th>{t("Patient")}</th>
                  <th>{t("Doctor")}</th>
                  <th>{t("Time / Chair")}</th>
                  <th>{t("Status")}</th>
                  <th className="text-end">{t("Action")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredQueue.map((item, idx) => {
                  const patient = item.patient || {};
                  const patientName = `${patient.title || ""} ${patient.first_name || ""} ${patient.last_name || ""}`.trim() ||
                    patient.full_name || t("Walk-in Patient");

                  const timeStr = item.arravel_time
                    ? moment(item.arravel_time).format("hh:mm A")
                    : item.appointment_date
                    ? moment(item.appointment_date).format("hh:mm A")
                    : moment(item.created_at).format("hh:mm A");

                  return (
                    <tr key={item.id || idx}>
                      <td>
                        <span className="badge bg-light text-primary border font-monospace font-size-11 px-2 py-0.5 fw-bold">
                          #{item.token || item.id || idx + 1}
                        </span>
                      </td>
                      <td>
                        <div className="fw-semibold text-dark font-size-13">
                          {patient.id ? (
                            <Link to={`/patient/details/${patient.id}`} className="text-dark">
                              {patientName}
                            </Link>
                          ) : (
                            patientName
                          )}
                        </div>
                        <div className="text-muted font-size-11 d-flex align-items-center gap-1.5 flex-wrap">
                          {patient.case_no && <span>#{patient.case_no}</span>}
                          {patient.mobile && <span>📞 {patient.mobile}</span>}
                        </div>
                      </td>
                      <td>
                        {item.doctor ? (
                          <span
                            className="badge font-size-11 px-2 py-1"
                            style={{
                              backgroundColor: item.doctor.color_code || "#556ee6",
                              color: "#fff"
                            }}
                          >
                            {item.doctor.name?.startsWith("Dr.") ? item.doctor.name : `Dr. ${item.doctor.name}`}
                          </span>
                        ) : (
                          <span className="text-muted font-size-11">—</span>
                        )}
                      </td>
                      <td>
                        <div className="font-size-11 font-weight-medium text-dark d-flex align-items-center gap-1">
                          <i className="mdi mdi-clock-outline text-muted" />
                          {timeStr}
                        </div>
                        {item.chair_code && (
                          <div className="font-size-10 text-muted">
                            Chair: {item.chair_code}
                          </div>
                        )}
                      </td>
                      <td>
                        <Badge
                          color={item.statusBadgeColor}
                          pill
                          className="font-size-11 px-2 py-1 d-inline-flex align-items-center gap-1"
                        >
                          <i className={item.statusIcon} />
                          {item.statusLabel}
                        </Badge>
                      </td>
                      <td className="text-end">
                        {patient.id ? (
                          <Button
                            color="light"
                            size="sm"
                            onClick={() => history.push(`/patient/details/${patient.id}`)}
                            className="btn-soft-primary font-size-11 p-1 px-2.5 rounded shadow-2xs"
                            title={t("Open Patient Chart & Treatments")}
                          >
                            {t("Open Case")} →
                          </Button>
                        ) : (
                          <Button
                            color="light"
                            size="sm"
                            onClick={() => history.push("/appointment")}
                            className="btn-soft-secondary font-size-11 p-1 px-2 rounded"
                          >
                            {t("View")}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default LiveWaitingRoomWidget;
